import { Box, Stack, useTheme } from "@mui/material";

import { useToast, AlertBanner, Weight20Px } from "@qubit/autoparts";

import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback } from "react";

import { useTranslation } from "react-i18next";

import { ScanningVerification } from "~/api/scanningVerifyMethod";
import { useAppDispatch, useAppSelector } from "~/app/store";
import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";
import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import envConstants from "~/config/envConstants";
import useFlag from "~/config/flags";

import { AlertInfo } from "~/features/inventory/components/AlertInfo";
import { useDisclosure } from "~/hooks/useDisclosure";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import { displayPickData } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { setIsPickQuantityConfirmed } from "~/redux/actions";
import { selectIsPickQuantityConfirmed } from "~/redux/selectors/autostoreSelectors";
import { selectAbleQtyToPick } from "~/redux/selectors/outOfStockSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import {
  selectWorkstationAutostoreGridId,
  selectWorkstationAvailablePorts,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import { useGetFocusedPickQuery } from "~/redux/warehouse/autostorePicking.hooks";

import {
  useCaptureWeightMutation,
  useVerifyPickMutation
} from "~/redux/warehouse/pick.hooks";

import { areAllPickWeightsCaptured } from "./AutostorePicking";
import { BarcodeInput } from "./BarcodeInput";

import {
  selectWeightsCapturedOnPickSoFar,
  selectWeightThresholdCheckResult,
  setWeightCaptureResponse
} from "./autostorePicking.slice";
import { setPickedQuantityModalStatus } from "./confirmPickQuantityModal/confirmPickQuantityModal.slice";
import { ScannedWeightChips } from "./variableWeight/ScannedWeightChips";
import { WeightEntryModal } from "./variableWeight/WeightEntryModalButton";

export default function FocusedPickInfoCard({
  onRemoveWeight,
  isRemovingWeight = false
}: {
  onRemoveWeight: (weightValue: number) => void;
  isRemovingWeight?: boolean;
}) {
  const dispatch = useAppDispatch();
  const trackedPageName = useMixpanelPageName();
  const { t } = useTranslation();
  const { palette } = useTheme();

  const [captureWeight] = useCaptureWeightMutation();

  const weightThresholdCheckResult = useAppSelector(
    selectWeightThresholdCheckResult
  );
  const weightsCapturedOnPickSoFar = useAppSelector(
    selectWeightsCapturedOnPickSoFar
  );

  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const pickQuantityConfirmationEnabled =
    fulfillmentCenter?.pickQuantityConfirmationEnabled ?? false;
  const workstationId = useAppSelector(selectWorkstationId);
  const gridId = useAppSelector(selectWorkstationAutostoreGridId);
  const isPickQuantityConfirmed = useAppSelector(selectIsPickQuantityConfirmed);
  const ableToPickQty = useAppSelector(selectAbleQtyToPick);

  // only expecting one parent port at this time since picking does not support independent multi-ports
  const [portId] = useAppSelector(selectWorkstationAvailablePorts);
  const portStatus = usePortStatusQuery(portId);
  const binIsPresent = portStatus.isReady || portStatus.isBinOpen;

  const { data: focusedPick, isLoading: isFocusedPickLoading } =
    useGetFocusedPickQuery(workstationId ? { workstationId } : skipToken);
  const { pickingNotes } = focusedPick || {};

  const [verifyPick] = useVerifyPickMutation();

  const { errorToast } = useToast();

  const { simulateUpcScan: simulateUpcScanFeatureFlag } = useFlag();

  const isWeightedPick = !!focusedPick?.weightThreshold;
  const pickQtyForWeights = ableToPickQty ?? focusedPick?.quantity?.value;
  const arePickWeightsReady =
    !isWeightedPick ||
    areAllPickWeightsCaptured({
      pickQty: pickQtyForWeights,
      weightsCapturedOnPickSoFar,
      weightThresholdCheckResult
    });
  const weightsLeftToCapture =
    (pickQtyForWeights ?? 0) - weightsCapturedOnPickSoFar.length;
  const isOverweight = weightThresholdCheckResult === "AboveMaximum";
  const isUnderweight =
    !weightsLeftToCapture && weightThresholdCheckResult === "BelowMinimum";
  const {
    open: isWeightEntryModalOpen,
    onOpen: openWeightEntryModal,
    onClose: closeWeightEntryModal
  } = useDisclosure();

  const handleWeightedPickBeforeVerify = useCallback(
    async (enteredUpc?: string): Promise<boolean> => {
      if (arePickWeightsReady || pickQtyForWeights === undefined) {
        return true;
      }

      if (enteredUpc) {
        try {
          const weightCaptureResult = await captureWeight({
            pickId: focusedPick.pickId,
            productCode: enteredUpc,
            weightsCapturedOnPickSoFar,
            entryMethod: "Manual",
            workstationId
          }).unwrap();

          dispatch(setWeightCaptureResponse(weightCaptureResult));

          const weightsReady = areAllPickWeightsCaptured({
            pickQty: pickQtyForWeights,
            weightsCapturedOnPickSoFar:
              weightCaptureResult.allWeightsCapturedOnPickSoFar,
            weightThresholdCheckResult:
              weightCaptureResult.weightThresholdCheckResult
          });
          if (!weightsReady) return false;
        } catch (error) {
          errorToast(getMessageFromRtkError(error));
          return false;
        }
      } else {
        if (
          weightsCapturedOnPickSoFar.length < pickQtyForWeights &&
          !isOverweight
        ) {
          openWeightEntryModal();
        }
        return false;
      }

      return true;
    },
    [
      arePickWeightsReady,
      captureWeight,
      dispatch,
      errorToast,
      focusedPick,
      isOverweight,
      openWeightEntryModal,
      pickQtyForWeights,
      weightsCapturedOnPickSoFar,
      workstationId
    ]
  );

  const matchEnteredUpcOrSku = useCallback(
    async (enteredUpc?: string) => {
      if (!focusedPick) return false;

      if (isWeightedPick) {
        const canVerifyPick = await handleWeightedPickBeforeVerify(enteredUpc);
        if (!canVerifyPick) return false;
      }

      const pickId = focusedPick.pickId;
      const finalUpcOrSku =
        enteredUpc || focusedPick.primaryUpc || focusedPick.sku;
      const shouldSkipQuantityConfirmation =
        ableToPickQty === 1 || focusedPick.quantity?.value === 1;

      mixpanelTrack({
        trackedPageName,
        type: enteredUpc ? "Scan" : "Button Click",
        label: "Confirm Pick",
        eventProperties: {
          "Pick Id": pickId,
          SKU: finalUpcOrSku,
          Quantity: focusedPick.quantity?.value
        }
      });

      try {
        await verifyPick({
          upcOrSku: finalUpcOrSku,
          pickId,
          verifyMethod: ScanningVerification.INPUT,
          workstationId: workstationId
        }).unwrap();

        if (shouldSkipQuantityConfirmation) {
          dispatch(setIsPickQuantityConfirmed(true));
          dispatch(
            setPickedQuantityModalStatus({
              isShown: false,
              pickedQuantity: null,
              selectedBinId: null
            })
          );

          // The user should be able to complete the pick from PTL also
          await displayPickData({
            portId,
            pickId,
            batchId: focusedPick.batchId,
            gridId
          });
        } else {
          // colin: PTL should be enabled later, but where?
          //  PTL should probably be enabled in response to the pick quantity confirmed = true or the choreography that manages that flow.
          dispatch(
            setPickedQuantityModalStatus({
              isShown: true,
              pickedQuantity: ableToPickQty || focusedPick.quantity?.value,
              selectedBinId: focusedPick.autostoreBinId
            })
          );
        }
        return true;
      } catch (error) {
        errorToast(getMessageFromRtkError(error));
        return false;
      }
    },
    [
      ableToPickQty,
      dispatch,
      errorToast,
      focusedPick,
      gridId,
      handleWeightedPickBeforeVerify,
      isWeightedPick,
      portId,
      trackedPageName,
      verifyPick,
      workstationId
    ]
  );

  const confirmationRequired =
    pickQuantityConfirmationEnabled &&
    simulateUpcScanFeatureFlag &&
    binIsPresent &&
    !isPickQuantityConfirmed;

  return (
    <Box mt={"1.25em"}>
      {!isFocusedPickLoading && focusedPick ? (
        <>
          <ProductCard.Root
            productName={focusedPick.productName}
            quantity={focusedPick.countOnHand?.value}
            imageFileName={focusedPick.imageFilename}
            onProductImageClick={matchEnteredUpcOrSku}
            disabled={!confirmationRequired}
            tempZone={focusedPick.temperatureZone}
            sx={
              isUnderweight || isOverweight
                ? {
                    backgroundColor: isOverweight
                      ? "error.light"
                      : isUnderweight
                        ? "warning.light"
                        : "white",
                    border: isOverweight
                      ? `4px solid ${palette.error.dark}`
                      : isUnderweight
                        ? `4px solid ${palette.warning.dark}`
                        : "none"
                  }
                : {}
            }
          >
            {pickQuantityConfirmationEnabled && (
              <BarcodeInput matchEnteredUpcOrSku={matchEnteredUpcOrSku} />
            )}
            <ProductCard.Sku sku={focusedPick.sku} />
            {pickingNotes && (
              <ASDSThemeProvider>
                <AlertBanner severity="info" sx={{ mt: 1 }}>
                  {pickingNotes}
                </AlertBanner>
              </ASDSThemeProvider>
            )}
            <Stack direction="row" gap={0}>
              <ProductCard.ProductCount
                count={{
                  value: focusedPick.unitAmount,
                  units: focusedPick.unitFormatted
                }}
              />
              {isWeightedPick && (
                <AlertInfo
                  type="warning"
                  message={t("weight required")}
                  icon={<Weight20Px />}
                />
              )}
            </Stack>
            {envConstants.SHOW_SCANNABLE_BAR_CODES === "true" && (
              <ProductCard.QRCode upc={focusedPick.primaryUpc} />
            )}
          </ProductCard.Root>
          {isWeightedPick && (
            <ScannedWeightChips
              weightThreshold={focusedPick.weightThreshold}
              onRemoveWeight={onRemoveWeight}
              isRemovingWeight={isRemovingWeight}
            />
          )}
          {isWeightedPick && isWeightEntryModalOpen && (
            <WeightEntryModal
              open={isWeightEntryModalOpen}
              onClose={closeWeightEntryModal}
            />
          )}
        </>
      ) : (
        <ProductCard.Loading innerHeight="32vH" />
      )}
    </Box>
  );
}
